import { Injectable } from '@angular/core';
import { AppConfigService } from './app-config.service';
import { FeaturesModel, DEFAULT_FEATURES } from '../models/features.model';

@Injectable({
  providedIn: 'root'
})
export class FeaturesService {
  constructor(private appConfigService: AppConfigService) {}

  private getFeatures(): FeaturesModel {
    const config = this.appConfigService.getConfig();
    // Merge config features with defaults to ensure all properties are defined
    return { ...DEFAULT_FEATURES, ...(config?.features || {}) };
  }

  isFeatureEnabled(feature: keyof FeaturesModel): boolean {
    const value = this.getFeatures()[feature];
    return typeof value === 'boolean' ? value : false;
  }

  isOrganizationRoleEnabled(roleId: number): boolean {
    return this.getFeatures().enabledOrganizationRoles.includes(roleId);
  }

  isIdentDocTypeEnabled(identDocTypeId: number): boolean {
    return this.getFeatures().enabledIdentDocTypes.includes(identDocTypeId);
  }

  isServicePointTypeEnabled(servicePointTypeId: number): boolean {
    return this.getFeatures().enabledServicePointTypes.includes(servicePointTypeId);
  }

  isVulnerableGroupEnabled(categoryId: number): boolean {
    return this.getFeatures().enabledVulnerableGroups.includes(categoryId);
  }

  getPhoneNumberPattern(): string {
    return this.getFeatures().phoneNumberPattern;
  }

  getPhoneCountryCode(): string {
    return this.getFeatures().phoneCountryCode;
  }

  /**
   * Returns the phone mask for input fields (ngx-mask format).
   * Used for formatting phone number input fields.
   * @returns Mask pattern like "(000) 000-0000"
   */
  getPhoneMask(): string {
    return this.getFeatures().phoneMask;
  }

  /**
   * Returns the phone prefix for display in input fields.
   * For Ukraine (380): returns "+38" (partial prefix for 10-digit local format)
   * For Kazakhstan (7): returns "+7" (full country code)
   * For other countries: returns "+" + full country code
   */
  getPhonePrefix(): string {
    const code = this.getFeatures().phoneCountryCode;
    // Ukraine uses special "+38" prefix (user enters 10 more digits)
    if (code === '380') {
      return '+38';
    }
    // Other countries use full code as prefix
    return '+' + code;
  }

  isCyrillicValidationEnabled(): boolean {
    return this.getFeatures().enableCyrillicCharactersNameFormatValidation;
  }

  isNameDictionaryValidationEnabled(): boolean {
    return this.getFeatures().enableNameDictionaryValidation;
  }

  showPactReports(): boolean {
    return this.getFeatures().showPactReports;
  }

  showMergeClientsAction(): boolean {
    return this.getFeatures().showMergeClientsAction;
  }

  useUicForAidsOrgCode(): boolean {
    return this.getFeatures().useUicForAidsOrgCode;
  }

  showUicInClientsRegister(): boolean {
    return this.getFeatures().showUicInClientsRegister;
  }

  showMiddleNameInClientsRegister(): boolean {
    return this.getFeatures().showMiddleNameInClientsRegister;
  }

  showSelfServiceControls(): boolean {
    return this.getFeatures().showSelfServiceControls;
  }

  getOrganizationTerritorialCommunityLabel(): string {
    return this.getFeatures().organizationTerritorialCommunityLabel;
  }

  isOrganizationSettlementRequired(): boolean {
    return this.getFeatures().organizationSettlementRequired;
  }

  isUAConfig(): boolean {
    return this.appConfigService.getDefaultLanguage() === 'ua';
  }

  getCurrency(): string {
    return this.getFeatures().currency;
  }

  isUserRoleEnabled(roleId: number): boolean {
    return this.getFeatures().enabledUserRoles.includes(roleId);
  }

  showOpenMapButton(): boolean {
    return this.getFeatures().showOpenMapBtn;
  }

  showIsAvailableForClientCheckbox(): boolean {
    return this.getFeatures().showIsAvailableForClientCheckbox;
  }

  showInformationForClientTab(): boolean {
    return this.getFeatures().showInformationForClientTab;
  }

  showIsAvailableForClientColumn(): boolean {
    return this.getFeatures().showIsAvailableForClientColumn;
  }

  showSelfServiceTab(): boolean {
    return this.getFeatures().showSelfServiceTab;
  }

  getAllFeatures(): FeaturesModel {
    return this.getFeatures();
  }
}
